import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MyButton from './buttons'
import { deleteAllUserData } from '../../services/deleteMyAccount'
import Modal from './MyModal'
import { useNavigate } from 'react-router-dom'
import { IoIosWarning } from 'react-icons/io'
import MyInput from './MyInput'
import { FaTrashAlt } from 'react-icons/fa'
import CookieManager from '../lib/CookiesManager'

interface DeleteAccountButtonProps {
  userId: string
  className?: string
}

const DeleteAccountButton: React.FC<DeleteAccountButtonProps> = ({
  userId,
  className,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isDeleting, setIsDeleting] = useState(false)
  const [isFirstModalOpen, setIsFirstModalOpen] = useState(false)
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false)
  const [isResultModalOpen, setIsResultModalOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState('')
  const [deleteSuccess, setDeleteSuccess] = useState(false)

  const handleDeleteAccount = async () => {
    if (confirmText.toLowerCase() !== 'delete') {
      setError(t('delete-account-confirm-text-error'))
      return
    }

    setIsDeleting(true)
    try {
      await deleteAllUserData(userId)
      setDeleteSuccess(true)
      setIsResultModalOpen(true)
      setIsSecondModalOpen(false)
      console.log('User account deleted successfully')
    } catch (err) {
      console.error('Error deleting user account:', err)
      setError(t('delete-account-error'))
      setIsResultModalOpen(true)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleFirstConfirm = () => {
    event?.preventDefault()
    setIsFirstModalOpen(false)
    setIsSecondModalOpen(true)
  }

  const resetModals = () => {
    event?.preventDefault()
    setIsFirstModalOpen(false)
    setIsSecondModalOpen(false)
    setIsResultModalOpen(false)
    setConfirmText('')
    setError('')
  }

  const handleCloseResultModal = () => {
    event?.preventDefault()
    resetModals()
    if (deleteSuccess) {
      CookieManager.clearAuth()
      CookieManager.removeAllCookies()
      localStorage.clear()
      navigate('/')
    }
  }

  return (
    <>
      <MyButton
        variant='redpink'
        onClick={() => setIsFirstModalOpen(true)}
        className={className || 'flex-1'}
        disabled={isDeleting}
        type='button'
      >
        <FaTrashAlt className='text-lg' />
        {t('delete-account')}
      </MyButton>

      <Modal isOpen={isFirstModalOpen} onClose={resetModals}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('delete-account')}
          </h2>
          <div className='space-y-4 p-5'>
            <IoIosWarning className='text-redpink text-5xl mx-auto flex-shrink-0' />
            <div className='flex items-start mb-4'>
              <p className='mb-2 text-center bg-redlight rounded-lg p-2 flex-grow'>
                {t('delete-account-warning')}
              </p>
            </div>
            <div className='flex justify-end gap-2'>
              <MyButton variant='anthracite-redpink' onClick={resetModals}>
                {t('cancel')}
              </MyButton>
              <MyButton
                variant='redpink-anthracite'
                onClick={handleFirstConfirm}
              >
                {t('continue')}
              </MyButton>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isSecondModalOpen} onClose={resetModals}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('final-warning')}
          </h2>
          <div className='space-y-4 p-5'>
            <IoIosWarning className='text-redpink text-5xl mx-auto flex-shrink-0' />
            <div className='flex items-start mb-4'>
              <div className='flex-grow'>
                <p className='mb-2 text-center bg-redlight rounded-lg p-2'>
                  {t('delete-account-type-delete')}
                </p>
              </div>
            </div>

            <div className='mb-4'>
              <MyInput
                label={t('confirm-text')}
                value={confirmText}
                onChange={setConfirmText}
                error={error}
              />
            </div>

            <div className='flex justify-end gap-2'>
              <MyButton variant='anthracite-redpink' onClick={resetModals}>
                {t('cancel')}
              </MyButton>
              <MyButton
                variant='redpink-anthracite'
                onClick={handleDeleteAccount}
                disabled={confirmText.toLowerCase() !== 'delete' || isDeleting}
              >
                {t('delete-permanently')}
              </MyButton>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isResultModalOpen} onClose={handleCloseResultModal}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {deleteSuccess
              ? t('delete-account-success')
              : t('delete-account-error')}
          </h2>
          <div className='space-y-4 p-5'>
            {error && (
              <div className='text-anthracite bg-redlight p-2 rounded-lg'>
                {error}
              </div>
            )}
            {deleteSuccess && (
              <div className='text-anthracite bg-greenlight p-2 rounded-lg'>
                {t('delete-account-success')}
              </div>
            )}
            <div className='flex justify-end'>
              <MyButton
                variant='redpink-anthracite'
                onClick={handleCloseResultModal}
              >
                {t('close')}
              </MyButton>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default DeleteAccountButton
